/**
 * Hook Utilities - LicitarsAI Visual
 * 
 * Utilitários para evitar erros de hook call e melhorar a segurança dos hooks.
 * 
 * Problemas comuns:
 * - useAppSelector dentro de callbacks assíncronos
 * - Hooks chamados condicionalmente
 * - Hooks chamados fora de componentes React
 * - Múltiplas instâncias de React
 */

import React from 'react';
import { store } from "@/store";
import { logger } from "./logger";

/**
 * Hook Safety Checker
 * Verifica se um hook está sendo usado corretamente
 */
export const useHookSafety = (hookName: string, componentName: string) => {
  // Verificar se estamos em um ambiente React válido
  if (typeof window !== 'undefined' && !(window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    logger.warn(`[${hookName}] ⚠️ Hook usado fora do contexto React - ${componentName}`);
  }
  
  // Log para debugging
  logger.debug(`[${hookName}] 🔧 Hook inicializado em ${componentName}`);
};

/**
 * Safe State Getter
 * Obtém estado do Redux de forma segura em callbacks assíncronos
 * 
 * ❌ PROBLEMÁTICO:
 * setTimeout(() => {
 *   const state = useAppSelector(state => state); // ERRO: Hook em callback
 * }, 1000);
 * 
 * ✅ CORRETO:
 * setTimeout(() => {
 *   const state = getReduxState(); // SEGURO: Acesso direto ao store
 * }, 1000);
 */
export const getReduxState = () => {
  try {
    return store.getState();
  } catch (error) {
    logger.error('[getReduxState] ❌ Erro ao obter estado do Redux:', error);
    return null;
  }
};

/**
 * Safe Dispatch
 * Dispatch seguro para uso em callbacks assíncronos
 */
export const safeDispatch = (action: any) => {
  try {
    return store.dispatch(action);
  } catch (error) {
    logger.error('[safeDispatch] ❌ Erro ao fazer dispatch:', error);
    return null;
  }
};

/**
 * Hook Call Validator
 * Valida se um hook está sendo chamado corretamente
 */
export const validateHookCall = (hookName: string, conditions: {
  isInComponent?: boolean;
  isInRender?: boolean;
  hasDependencies?: boolean;
}) => {
  const errors: string[] = [];
  
  if (conditions.isInComponent === false) {
    errors.push('Hook deve ser chamado dentro de um componente React');
  }
  
  if (conditions.isInRender === false) {
    errors.push('Hook deve ser chamado durante o render');
  }
  
  if (conditions.hasDependencies === false) {
    errors.push('Hook deve ter dependências válidas');
  }
  
  if (errors.length > 0) {
    logger.error(`[${hookName}] ❌ Validação falhou:`, errors);
    return false;
  }
  
  return true;
};

/**
 * Async Hook Wrapper
 * Wrapper seguro para hooks que precisam de operações assíncronas
 */
export const useAsyncHook = <T>(
  asyncFn: () => Promise<T>,
  dependencies: any[] = []
) => {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  
  const execute = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await asyncFn();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      logger.error('[useAsyncHook] ❌ Erro na execução:', err);
    } finally {
      setLoading(false);
    }
  }, dependencies);
  
  return { data, loading, error, execute };
};

/**
 * WebSocket Hook Safety
 * Utilitários específicos para hooks WebSocket
 */
export const useWebSocketSafety = (sessionId: string | null) => {
  // Validar sessionId
  if (!sessionId) {
    logger.warn('[useWebSocketSafety] ⚠️ SessionId não fornecido');
  }
  
  // Verificar se estamos em um contexto válido
  const isValidContext = React.useContext !== undefined;
  if (!isValidContext) {
    logger.error('[useWebSocketSafety] ❌ Contexto React não disponível');
  }
  
  return {
    isValid: !!sessionId && isValidContext,
    sessionId,
  };
};

/**
 * Redux Hook Safety
 * Utilitários específicos para hooks Redux
 */
export const useReduxSafety = () => {
  // Verificar se o store está disponível
  const isStoreAvailable = store && typeof store.getState === 'function';
  if (!isStoreAvailable) {
    logger.error('[useReduxSafety] ❌ Store Redux não disponível');
  }
  
  return {
    isStoreAvailable,
    getState: getReduxState,
    dispatch: safeDispatch,
  };
};

/**
 * Hook Error Boundary
 * Captura erros específicos de hooks
 */
export const useHookErrorBoundary = (hookName: string) => {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  
  const handleError = React.useCallback((err: Error) => {
    logger.error(`[${hookName}] ❌ Erro capturado:`, err);
    setError(err);
    setHasError(true);
  }, [hookName]);
  
  const resetError = React.useCallback(() => {
    setError(null);
    setHasError(false);
  }, []);
  
  return {
    hasError,
    error,
    handleError,
    resetError,
  };
};

/**
 * Performance Hook Monitor
 * Monitora performance de hooks
 */
export const useHookPerformance = (hookName: string) => {
  const startTime = React.useRef<number>(Date.now());
  const renderCount = React.useRef<number>(0);
  
  React.useEffect(() => {
    renderCount.current += 1;
    const duration = Date.now() - startTime.current;
    
    if (duration > 100) { // Log se demorar mais de 100ms
      logger.warn(`[${hookName}] ⚠️ Hook lento: ${duration}ms (render #${renderCount.current})`);
    }
    
    startTime.current = Date.now();
  });
  
  return {
    renderCount: renderCount.current,
    resetTimer: () => {
      startTime.current = Date.now();
    },
  };
};

export default {
  useHookSafety,
  getReduxState,
  safeDispatch,
  validateHookCall,
  useAsyncHook,
  useWebSocketSafety,
  useReduxSafety,
  useHookErrorBoundary,
  useHookPerformance,
}; 